import { ProfileSetup } from 'components/Profile/ProfileSetup';
import { useCallback, useEffect, useState } from 'react';
import { phoneVerificationService } from 'services/phoneVerificationService';
import { useUserContext } from 'stores/user';

/**
 * Opens the profile-setup flow when the API refuses a request because the user's phone is
 * unverified.
 *
 * Booking-referrer users must confirm their phone by SMS before their first inquiry can be
 * dispatched. The profile-setup modal on the dashboard only opens for users with no phone
 * saved at all, so anyone holding an unverified number had no route to the verification
 * step and no way out of the refusal — reported by a B4B user who was stuck in that loop.
 *
 * Mounted app-wide beside the authenticated routes, because an inquiry can be submitted
 * from several places and the refusal has to be answerable wherever it happens.
 */
export function PhoneVerificationGate() {
    const { user, refreshUser } = useUserContext();
    const [open, setOpen] = useState(false);

    const handleRequired = useCallback(() => setOpen(true), []);

    useEffect(() => {
        phoneVerificationService.registerPhoneVerificationRequiredHandler(handleRequired);

        return () => {
            phoneVerificationService.unregisterPhoneVerificationRequiredHandler(handleRequired);
        };
    }, [handleRequired]);

    /**
     * Verifying elsewhere — another tab, or the dashboard's own setup modal — should close
     * this without the user having to complete the flow twice.
     */
    useEffect(() => {
        if (user?.phoneVerifiedAt) {
            setOpen(false);
        }
    }, [user?.phoneVerifiedAt]);

    const handleSuccess = useCallback(() => {
        setOpen(false);
        // Pull the fresh phoneVerifiedAt so a retry is not refused again by stale state.
        refreshUser?.();
    }, [refreshUser]);

    if (!open) {
        return null;
    }

    return <ProfileSetup open onSuccess={handleSuccess} />;
}
